import { Box, TableBody, TableCell, TableRow } from '@mui/material'
import { Actions, Field } from './types'
import { GenericTooltip } from '../generic-tooltip'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@material-ui/lab'

export type BodyProps<T> = {
  readonly fields: Field<T>[]
  readonly items: T[]
  readonly rowKeyField: keyof T
  readonly actions?: Actions<T>
  readonly isLoading?: boolean
}

export function BodyTreatment<T extends { [key: string]: any }>(props: BodyProps<T>) {
  const { t } = useTranslation()

  const totalColumns = props.fields.length + (props.actions?.items?.length ? 1 : 0)
  const columnWidth = `${100 / totalColumns}%`

  const rows = props.items.map((item, i) => {
    const actionStyle = props.actions?.styleFunc ? props.actions.styleFunc(item) : ''

    return !props.isLoading ? (
      <TableRow
        key={item[props.rowKeyField] + i}
        sx={{
          '& td': {
            padding: '16px',
            fontSize: '14px',
            verticalAlign: 'initial',
          },
          '& td:first-of-type': {
            fontWeight: 600,
            color: 'var(--green-primary)',
          },
        }}
      >
        {Object.values(props.fields).map((field, i) => {
          const style = field.styleFunc ? field.styleFunc(field, item) : ''

          return (
            <TableCell style={{ width: columnWidth }} key={(field.name as string) + i + 'cell'}>
              {field.renderFunc ? field.renderFunc(field, item) : item[field.name]?.toString()}
            </TableCell>
          )
        })}

        {props.actions && props.actions.items?.length > 0 && (
          <TableCell
            style={{
              width: columnWidth,
              marginLeft: 4,
            }}
            className={actionStyle}
            key={item[props.rowKeyField] + 'cell'}
          >
            <Box
              key={item[props.rowKeyField] + 'box'}
              sx={{ display: 'flex', gap: '20px', justifyContent: 'flex-start' }}
            >
              {props.actions.items.map((a, i) => {
                if (a.hidden && a.hidden(item)) {
                  return null
                }
                return (
                  <GenericTooltip
                    key={i}
                    values={[t(a.label || a.icon)]}
                    icon={
                      <img
                        src={a.icon}
                        style={{ verticalAlign: 'middle', cursor: 'pointer', padding: '0 4px' }}
                        alt="action"
                        onClick={() => a.handler(item)}
                      />
                    }
                    noUseFab={true}
                  />
                )
              })}
            </Box>
          </TableCell>
        )}
      </TableRow>
    ) : (
      <TableRow key={item[props.rowKeyField] + i}>
        {Object.values(props.fields).map((field, i) => {
          return (
            <TableCell style={{ width: columnWidth }} key={(field.name as string) + i + 'cell'}>
              <Skeleton />
            </TableCell>
          )
        })}

        {props.actions && props.actions.items?.length > 0 && (
          <TableCell style={{ width: columnWidth }} key={item[props.rowKeyField] + i + 'cellq'}>
            <Box sx={{ display: 'flex', gap: '20px', justifyContent: 'flex-start' }}>
              {props.actions.items.map((a, index) => {
                if (a.hidden && a.hidden(item)) {
                  return null
                }
                return (
                  <GenericTooltip
                    key={index}
                    values={[t(a.label || a.icon)]}
                    icon={<Skeleton variant="circle" width={10} height={10} />}
                    noUseFab={true}
                  />
                )
              })}
            </Box>
          </TableCell>
        )}
      </TableRow>
    )
  })

  return <TableBody>{rows}</TableBody>
}
